import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import {
  activeViewStatusCountsBaseQueryKey,
  supportTicketCountsQueryKey,
  supportTicketQueryScopeKey,
  supportTicketsPageScopedQueryKey,
} from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import type { QueryClient } from "@tanstack/react-query";
import { supportTicketSummaryBaseQueryKey } from "./db/support-ticket-data-source";

// Counts/lists are served from OpenSearch, which reindexes asynchronously after
// the CRDB write. A single delayed refetch can land before the index catches up
// and read stale data, with no retry — leaving sidebar counts frozen until a
// manual refresh (especially when the realtime event stream is unavailable).
// Fire several staggered invalidations so a refetch reliably lands after the
// reindex completes.
const SUPPORT_TICKET_LIST_INVALIDATION_DELAYS_MS = [
  2000, 6000, 15_000,
] as const;

const pendingInvalidationTimers = new WeakMap<
  QueryClient,
  Map<string, ReturnType<typeof setTimeout>[]>
>();

function queryScopeTimerKey(queryScope?: SupportTicketQueryScope): string {
  return JSON.stringify(supportTicketQueryScopeKey(queryScope));
}

export async function invalidateSupportTicketListQueriesNow({
  queryClient,
  queryScope,
}: {
  queryClient: QueryClient;
  queryScope?: SupportTicketQueryScope;
}): Promise<void> {
  // Awaited so callers (e.g. the refresh button) can keep a spinner up until the
  // refetch actually settles. Fire-and-forget callers can `void` the result.
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: supportTicketsPageScopedQueryKey(queryScope),
    }),
    queryClient.invalidateQueries({ queryKey: supportTicketCountsQueryKey }),
    queryClient.invalidateQueries({
      queryKey: activeViewStatusCountsBaseQueryKey,
    }),
    queryClient.invalidateQueries({
      queryKey: supportTicketSummaryBaseQueryKey,
    }),
  ]);
}

export function invalidateSupportTicketListQueriesAfterDelay({
  queryClient,
  queryScope,
}: {
  queryClient: QueryClient;
  queryScope?: SupportTicketQueryScope;
}) {
  const scopeKey = queryScopeTimerKey(queryScope);
  let pendingTimers = pendingInvalidationTimers.get(queryClient);
  if (!pendingTimers) {
    pendingTimers = new Map();
    pendingInvalidationTimers.set(queryClient, pendingTimers);
  }

  // A staggered burst is already scheduled for this scope; its later refetches
  // will also capture this mutation's change, so don't stack a second burst.
  if (pendingTimers.has(scopeKey)) {
    return;
  }

  const lastIndex = SUPPORT_TICKET_LIST_INVALIDATION_DELAYS_MS.length - 1;
  const timers = SUPPORT_TICKET_LIST_INVALIDATION_DELAYS_MS.map(
    (delay, index) =>
      setTimeout(() => {
        // Clear the pending entry once the final refetch fires so a later
        // mutation can schedule a fresh burst.
        if (index === lastIndex) {
          pendingTimers.delete(scopeKey);
        }
        void invalidateSupportTicketListQueriesNow({ queryClient, queryScope });
      }, delay),
  );
  pendingTimers.set(scopeKey, timers);
}
